import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Maximize2, Minimize2, Lock, Sparkles, Share, X, Cake } from 'lucide-react';
import { useFullscreenLock, PinPopup, enterFullscreen } from '../components/clock/FullscreenLock';
import { groupByDept, isStandalone, useHomeScreenMeta, useAutoFit, deptColor } from '../components/clock/displayHelpers';

const InstallHint = ({ accent }) => {
  const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const [show, setShow] = useState(ios && !isStandalone() && !localStorage.getItem('cd-hint-dismissed'));
  if (!show) return null;
  return (
    <div className="fixed bottom-6 right-6 z-40 max-w-sm rounded-2xl border border-white/15 bg-slate-900/90 p-4 text-white shadow-2xl" data-testid="display-install-hint">
      <button className="absolute top-2 right-2 text-white/50" onClick={() => { localStorage.setItem('cd-hint-dismissed', '1'); setShow(false); }} data-testid="display-install-hint-close"><X className="h-4 w-4" /></button>
      <p className="font-semibold" style={{ color: accent }}>Add to Home Screen</p>
      <p className="mt-1 text-sm text-white/70">Tap <Share className="inline h-4 w-4 -mt-1" /> Share, then <b>Add to Home Screen</b>, to open this display full-screen like an app.</p>
    </div>
  );
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api/clock/public/display`;
// Safari can't parse 6-digit microseconds, so trim to milliseconds
const toDate = (ts) => new Date(String(ts).replace(/(\.\d{3})\d+/, '$1'));
const hhmm = (ts) => {
  if (!ts) return '';
  const d = toDate(ts);
  return isNaN(d) ? '' : `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};
const keyOf = (s) => `${s.name}|${s.clock_in}`;

// One mood per day, the same on every screen
const THEMES = [
  { name: 'aurora', base: 'linear-gradient(135deg,#031b26 0%,#062a3a 45%,#0b1530 100%)', blobs: ['#14b8a6', '#22d3ee', '#6366f1'], accent: '#2dd4bf' },
  { name: 'sunrise', base: 'linear-gradient(160deg,#2a0f2e 0%,#4a1538 45%,#140b2b 100%)', blobs: ['#fb7185', '#f59e0b', '#a855f7'], accent: '#fbbf24' },
  { name: 'night', base: 'linear-gradient(180deg,#050b24 0%,#0b1640 60%,#050817 100%)', blobs: ['#3b82f6', '#8b5cf6', '#0ea5e9'], accent: '#60a5fa' },
  { name: 'forest', base: 'linear-gradient(140deg,#03170f 0%,#0a2a1c 50%,#06140f 100%)', blobs: ['#22c55e', '#84cc16', '#10b981'], accent: '#4ade80' },
  { name: 'ocean', base: 'linear-gradient(150deg,#021428 0%,#053259 50%,#03101f 100%)', blobs: ['#06b6d4', '#38bdf8', '#2563eb'], accent: '#22d3ee' },
  { name: 'ember', base: 'linear-gradient(135deg,#1c0a05 0%,#3a1208 50%,#120806 100%)', blobs: ['#f97316', '#ef4444', '#eab308'], accent: '#fb923c' },
];
const dayIndex = () => { const d = new Date(); return Math.floor((d - new Date(d.getFullYear(), 0, 0)) / 864e5); };

const CSS = `
@keyframes cd-drift1{0%,100%{transform:translate(0,0) scale(1)}50%{transform:translate(12vw,8vh) scale(1.2)}}
@keyframes cd-drift2{0%,100%{transform:translate(0,0) scale(1.1)}50%{transform:translate(-10vw,-6vh) scale(.9)}}
@keyframes cd-drift3{0%,100%{transform:translate(0,0)}50%{transform:translate(6vw,-10vh)}}
@keyframes cd-banner{0%{opacity:0;transform:translateY(30px) scale(.9)}12%{opacity:1;transform:none}85%{opacity:1}100%{opacity:0;transform:translateY(-20px)}}
@keyframes cd-glow{0%{box-shadow:0 0 0 0 var(--acc);transform:scale(1.04)}100%{box-shadow:0 0 0 0 transparent;transform:scale(1)}}
@keyframes cd-pop{0%{opacity:0;transform:translateY(12px)}100%{opacity:1;transform:none}}
.cd-new{animation:cd-glow 8s ease-out;border-color:var(--acc)!important;background:rgba(255,255,255,.16)!important}
@keyframes cd-fall{0%{transform:translateY(-5vh) rotate(0);opacity:1}100%{transform:translateY(105vh) rotate(720deg);opacity:.8}}
.cd-bday{border-color:rgba(244,114,182,.55)!important;background:linear-gradient(135deg,rgba(236,72,153,.22),rgba(245,158,11,.18))!important}
`;

const Background = ({ t }) => (
  <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none" style={{ background: t.base }} data-testid={`display-theme-${t.name}`}>
    {t.blobs.map((c, i) => (
      <div key={c} className="absolute rounded-full opacity-50"
        style={{ background: `radial-gradient(circle, ${c} 0%, ${c}00 70%)`, width: '55vw', height: '55vw', willChange: 'transform', top: ['-10%', '40%', '10%'][i], left: ['-10%', '55%', '30%'][i],
          animation: `cd-drift${i + 1} ${[22, 28, 34][i]}s ease-in-out infinite` }} />
    ))}
    <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at center,transparent 40%,rgba(0,0,0,.55) 100%)' }} />
  </div>
);

const Ring = ({ pct, accent }) => {
  const r = 70, c = 2 * Math.PI * r;
  return (
    <div className="relative" style={{ width: '11.25rem', height: '11.25rem' }}>
      <svg viewBox="0 0 180 180" width="100%" height="100%" className="-rotate-90">
        <circle cx="90" cy="90" r={r} fill="none" stroke="rgba(255,255,255,.12)" strokeWidth="14" />
        <circle cx="90" cy="90" r={r} fill="none" stroke={accent} strokeWidth="14" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c * (1 - pct / 100)} style={{ transition: 'stroke-dashoffset .8s ease', filter: `drop-shadow(0 0 8px ${accent})` }} />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-4xl font-bold" style={{ color: accent }} data-testid="display-percent">{pct}%</span>
    </div>
  );
};

const CONFETTI_COLORS = ['#f43f5e', '#f59e0b', '#22c55e', '#3b82f6', '#a855f7', '#ec4899', '#facc15'];
const CONFETTI = Array.from({ length: 70 }, (_, i) => ({
  left: (i * 37) % 100, delay: (i % 10) * 0.12, dur: 2.2 + (i % 5) * 0.3,
  color: CONFETTI_COLORS[i % CONFETTI_COLORS.length], w: 8 + (i % 3) * 4, round: i % 4 === 0,
}));

const Confetti = () => (
  <div className="fixed inset-0 z-40 overflow-hidden pointer-events-none" data-testid="display-confetti">
    {CONFETTI.map((c, i) => (
      <span key={i} className="absolute top-0 block" style={{
        left: `${c.left}%`, width: c.w, height: c.round ? c.w : c.w * 1.8, background: c.color,
        borderRadius: c.round ? '50%' : 2, animation: `cd-fall ${c.dur}s linear ${c.delay}s both`,
      }} />
    ))}
  </div>
);

const BirthdayBanner = ({ person }) => (
  <>
    <Confetti />
    <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none" data-testid="display-birthday-banner">
      <div key={keyOf(person)} className="rounded-3xl border-2 border-pink-300/60 bg-gradient-to-br from-fuchsia-900/90 via-rose-900/90 to-amber-900/90 px-20 py-12 text-center"
        style={{ animation: 'cd-banner 4s ease-in-out forwards', boxShadow: '0 0 100px #f472b680' }}>
        <Cake className="mx-auto h-16 w-16 mb-3 text-amber-300" />
        <p className="text-3xl text-pink-100/80">Happy Birthday,</p>
        <p className="text-7xl font-black text-white mt-1" data-testid="display-welcome-name">{person.name.split(' ')[0]}!</p>
        <p className="text-2xl text-pink-100/70 mt-3">Wishing you a wonderful day · clocked in at {hhmm(person.clock_in)}</p>
      </div>
    </div>
  </>
);

const WelcomeBanner = ({ person, accent }) => person.is_birthday ? <BirthdayBanner person={person} /> : (
  <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none" data-testid="display-welcome-banner">
    <div key={keyOf(person)} className="rounded-3xl border border-white/20 bg-slate-900/75 px-20 py-12 text-center shadow-2xl"
      style={{ animation: 'cd-banner 4s ease-in-out forwards', boxShadow: `0 0 80px ${accent}55` }}>
      <Sparkles className="mx-auto h-12 w-12 mb-3" style={{ color: accent }} />
      <p className="text-3xl text-white/70">Welcome,</p>
      <p className="text-7xl font-black text-white mt-1" data-testid="display-welcome-name">{person.name.split(' ')[0]}!</p>
      <p className="text-2xl text-white/60 mt-3">{person.name} · clocked in at {hhmm(person.clock_in)}</p>
    </div>
  </div>
);

export default function ClockDisplay() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const [now, setNow] = useState(new Date());
  const [queue, setQueue] = useState([]);
  const [fresh, setFresh] = useState({});
  const seen = useRef(null);
  const t = THEMES[dayIndex() % THEMES.length];
  useHomeScreenMeta(token);
  const fitRef = useAutoFit([d, err]);
  const fs = useFullscreenLock(!!d?.pin_enabled);

  const load = useCallback(() => {
    axios.get(`${API}/${token}`).then((r) => {
      const staff = r.data.present_staff;
      if (seen.current) {
        const added = staff.filter((s) => !seen.current.has(keyOf(s)));
        if (added.length) {
          setQueue((q) => [...q, ...added]);
          setFresh((f) => { const n = { ...f }; added.forEach((s) => { n[keyOf(s)] = Date.now(); }); return n; });
        }
      }
      seen.current = new Set(staff.map(keyOf));
      setD(r.data); setErr('');
    }).catch((e) => setErr(e.response?.status === 404 ? e.response.data.detail : 'Reconnecting…'));
  }, [token]);

  useEffect(() => {
    load();
    const a = setInterval(load, 15000);
    const b = setInterval(() => setNow(new Date()), 1000);
    return () => { clearInterval(a); clearInterval(b); };
  }, [load]);

  useEffect(() => {
    if (!queue.length) return;
    const id = setTimeout(() => setQueue((q) => q.slice(1)), 4000);
    return () => clearTimeout(id);
  }, [queue]);

  const isFresh = (s) => fresh[keyOf(s)] && now - fresh[keyOf(s)] < 8000 + queue.length * 4000;

  if (!d) return (
    <div className="min-h-screen flex items-center justify-center text-2xl text-white/60" data-testid="display-error">
      <Background t={t} /><span className="relative z-10">{err || 'Loading…'}</span>
    </div>
  );
  return (
    <div className="h-[100dvh] overflow-hidden text-white relative" style={{ '--acc': t.accent }} data-testid="clock-display-page">
      <style>{CSS}</style>
      <Background t={t} />
      {queue[0] && <WelcomeBanner key={keyOf(queue[0])} person={queue[0]} accent={t.accent} />}
      {fs.prompt && <PinPopup token={token} isFs={fs.isFs} onUnlock={fs.unlock} onClose={() => fs.setPrompt(false)} />}
      <div ref={fitRef} className="relative z-10 p-10 pb-14 flex flex-col gap-8">
      <header className="relative z-10 flex flex-wrap items-center gap-10">
        <Ring pct={d.percent} accent={t.accent} />
        <div>
          <p className="text-lg uppercase tracking-[0.3em] text-white/60">Clocked in now</p>
          <p className="text-8xl font-black leading-none" data-testid="display-present">{d.present}<span className="text-4xl text-white/40 font-semibold"> / {d.total}</span></p>
        </div>
        <div className="ml-auto text-right">
          <p className="text-6xl font-bold tabular-nums" data-testid="display-clock">{now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}</p>
          <p className="text-xl text-white/60">{now.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
          {!isStandalone() && (fs.isFs ? (
            <button onClick={() => (fs.locked ? fs.setPrompt(true) : fs.unlock())} className="mt-3 inline-flex items-center gap-2 text-sm text-white/40 hover:text-white" data-testid="display-exit-fullscreen-btn">
              {fs.locked ? <Lock className="h-4 w-4" /> : <Minimize2 className="h-4 w-4" />} Exit full screen
            </button>
          ) : (
            <button onClick={enterFullscreen} className="mt-3 inline-flex items-center gap-2 text-sm text-white/40 hover:text-white" data-testid="display-fullscreen-btn">
              <Maximize2 className="h-4 w-4" /> Full screen{d.pin_enabled ? ' (PIN locked)' : ''}
            </button>
          ))}
        </div>
      </header>
      {err && <p className="relative z-10 text-amber-300 text-lg">{err}</p>}
      <main className="relative z-10 flex flex-col gap-6" data-testid="display-staff-grid">
        {d.present_staff.length === 0 && <p className="text-3xl text-white/50">No one is clocked in right now.</p>}
        {groupByDept(d.present_staff).map(([dept, staff]) => { const dc = deptColor(dept, d.dept_colors); return (
          <section key={dept} data-testid={`display-dept-${dept}`}>
            <h2 className="mb-3 flex items-center gap-3 text-xl font-bold uppercase tracking-[0.2em]" style={{ color: dc, textShadow: `0 0 18px ${dc}66` }}>
              <span className="h-4 w-4 rounded-sm" style={{ background: dc }} />
              {dept} <span className="text-white/40 text-base font-semibold">({staff.length})</span>
              <span className="h-px flex-1" style={{ background: `linear-gradient(90deg, ${dc}99, transparent)` }} />
            </h2>
            <div className="grid grid-cols-[repeat(auto-fill,minmax(min(21rem,100%),1fr))] gap-4">
              {staff.map((s) => (
                <div key={keyOf(s)} className={`flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.08] px-5 py-4 ${s.is_birthday ? 'cd-bday' : ''} ${isFresh(s) ? 'cd-new' : ''}`}
                  style={{ borderLeft: `0.4rem solid ${dc}`, animation: isFresh(s) ? undefined : `cd-pop .5s ease-out ${Math.min(s.i, 20) * 40}ms both` }} data-testid={`display-staff-${s.i}`}>
                  <span className="h-3 w-3 rounded-full flex-shrink-0" style={{ background: dc, boxShadow: `0 0 12px ${dc}` }} />
                  <span className="text-2xl font-semibold leading-tight flex-1 break-words">{s.name}</span>
                  {s.is_birthday && <Cake className="h-7 w-7 text-amber-300 flex-shrink-0" title="Birthday today" data-testid={`display-bday-badge-${s.i}`} />}
                  <span className="text-xl text-white/60 tabular-nums">{hhmm(s.clock_in)}</span>
                </div>
              ))}
            </div>
          </section>
        ); })}
      </main>
      </div>
      <InstallHint accent={t.accent} />
      <footer className="absolute bottom-4 left-10 z-10 text-sm text-white/30">Updates automatically every 15 seconds · today's theme: {t.name}</footer>
    </div>
  );
}
